(ns practitest.frontend.components2.dashboard.charts.label-simulation
 (:require ["d3" :as d3]))

(defn- outside-ring-force
  "Hard constraint keeping labels outside the label ring
   (the invisible circle at radius * label-ring-multiplier where labels
   are initially placed before simulation). Soft forces (radial, collide)
   can't guarantee this on their own — collisions can push labels inward
   past the ring."
  [nodes ring-radius]
  (fn [_alpha]
    (doseq [^js node nodes]
      (let [dist (js/Math.hypot (.-x node) (.-y node))]
        (when (< dist ring-radius)
          (let [s (/ ring-radius dist)]
            (set! (.-x node) (* (.-x node) s))
            (set! (.-y node) (* (.-y node) s))))))))

(defn- bounds-force
  "Hard constraint clamping labels inside the visible SVG area.
   Zeroes velocity on clamped axes so D3's velocity step doesn't
   push labels back out."
  [nodes {:keys [half-w top bottom]}]
  (fn [_alpha]
    (doseq [^js node nodes]
      (let [cx (max (- half-w) (min half-w (.-x node)))
            cy (max (- top) (min bottom (.-y node)))]
        (when (not= cx (.-x node))
          (set! (.-x node) cx)
          (set! (.-vx node) 0))
        (when (not= cy (.-y node))
          (set! (.-y node) cy)
          (set! (.-vy node) 0))))))

(defn- rect-collide-force
  "Rectangular collision force for label nodes.

   D3's forceCollide uses a single circle radius — it enforces the
   same minimum distance in every direction. Labels are wide and
   short, so no single radius works:

   - Small radius (sized for height): circles don't touch, so
     forceCollide does nothing — but the text extends far beyond
     the circle and overlaps with the neighbor's text:

         ╭─────╮
         │  ·  │ Ivan 934
         ╰──┬──╯
            │ circles don't touch, but text overlaps!
         ╭──┴──╮
         │  ·  │ Alex 211
         ╰─────╯

   - Large radius (sized for width): prevents horizontal overlap,
     but forces labels too far apart vertically because the circle
     enforces the same large distance in all directions.

   Rectangular collision solves this by checking overlap on each
   axis independently (pad-x wide, pad-y tall):
   When two rects overlap, pushes them apart on the axis with
   less overlap (minimum displacement).

   pad-x/pad-y define the bounding rectangle around each label's
   center. Two labels collide when their rectangles overlap, i.e.
   when centers are closer than pad-x on X AND pad-y on Y
   simultaneously."
  [nodes pad-x pad-y]
  (fn [_alpha]
    (let [n (alength nodes)]
      ;; Check every unique pair of nodes for overlap
      (dotimes [i n]
        (dotimes [k (- n 1 i)]
          (let [^js a (aget nodes i)
                ^js b (aget nodes (+ i 1 k))
                ;; How much the bounding boxes overlap on each axis
                ;; (positive = overlapping, negative = separated)
                ox    (- pad-x (abs (- (.-x a) (.-x b))))
                oy    (- pad-y (abs (- (.-y a) (.-y b))))]
            ;; Only act when boxes overlap on BOTH axes
            (when (and (pos? ox) (pos? oy))
              ;; Push apart on the axis with less overlap (least disruption)
              ;; Each node moves half the overlap distance
              (if (< oy ox)
                (let [shift (* 0.5 oy (if (neg? (- (.-y a) (.-y b))) -1 1))]
                  (set! (.-y a) (+ (.-y a) shift))
                  (set! (.-y b) (- (.-y b) shift)))
                (let [shift (* 0.5 ox (if (neg? (- (.-x a) (.-x b))) -1 1))]
                  (set! (.-x a) (+ (.-x a) shift))
                  (set! (.-x b) (- (.-x b) shift)))))))))))

(defn- angle-from-top
  "Angle of point [x y] around the pie center, clockwise from 12 o'clock,
   in [0, 2π) — the same convention d3.pie uses for slice angles."
  [x y]
  (let [a (js/Math.atan2 x (- y))]
    (if (neg? a) (+ a (* 2 js/Math.PI)) a)))

(defn- restore-order!
  "Forces can push a label past its neighbor, so labels end up in a
   different order than their slices and the connectors cross.
   Reassigns the final positions so that, going clockwise around the
   pie, labels follow the same order as their anchors (targetX/Y).
   Only the order changes: the set of positions stays the same, so the
   collision-free spacing is kept."
  [nodes]
  (let [by-anchor (vec (sort-by (fn [^js node] (angle-from-top (.-targetX node) (.-targetY node))) nodes))
        positions (vec (sort-by (fn [[x y]] (angle-from-top x y))
                                (map (fn [^js node] [(.-x node) (.-y node)]) nodes)))
        n         (count by-anchor)
        position  (fn [shift i] (nth positions (mod (+ i shift) n)))
        cost      (fn [shift]
                    (reduce + (map-indexed (fn [i ^js node]
                                             (let [[x y] (position shift i)]
                                               (+ (js/Math.pow (- x (.-targetX node)) 2)
                                                  (js/Math.pow (- y (.-targetY node)) 2))))
                                           by-anchor)))]
    (when (pos? n)
      (let [shift (apply min-key cost (range n))]
        (doseq [[i ^js node] (map-indexed vector by-anchor)]
          (let [[x y] (position shift i)]
            (set! (.-x node) x)
            (set! (.-y node) y)))))))

(defn run-label-simulation
  "Run D3 force simulation to compute collision-free label positions.

   nodes  — JS array of node objects (mutated in place by D3)
   config — static tuning values (angle-strength, force-iterations, etc.)
   ctx    — computed values (ring-radius, bounds)

   Each node is a JS object with:
     x, y       — current position (mutated in place by D3 each tick).
                   Initialized to the label ring centroid, but diverges
                   as forces push nodes apart.
     targetX/Y  — fixed anchor (never mutated). Same value as the initial
                   x/y. Forces pull the node back toward this point so
                   labels stay near their slice's angle.
     label      — label text string, used as key for position lookup.
     vx, vy     — velocity (added by D3 automatically)."
  [nodes {:keys [angle-strength force-iterations label-collide-x label-collide-y]}
   {:keys [ring-radius bounds]}]
  (let [sim (-> (.forceSimulation d3 nodes)
                (.force "charge" (-> (.forceManyBody d3)
                                     (.strength -400)
                                     (.distanceMax 400)))
                ;; Angle pull: keeps labels near their slice's angular position.
                ;; forceX/forceY together pull each node toward its targetX/Y
                ;; (the initial position on the label ring). Without this,
                ;; charge + collide would scatter labels freely.
                (.force "x" (-> (.forceX d3)
                                (.x (fn [^js d] (.-targetX d)))
                                (.strength angle-strength)))
                (.force "y" (-> (.forceY d3)
                                (.y (fn [^js d] (.-targetY d)))
                                (.strength angle-strength)))
                (.force "outside-ring" (outside-ring-force nodes ring-radius))
                (.force "rect-collide" (rect-collide-force nodes
                                                           label-collide-x
                                                           label-collide-y))
                (.force "bounds" (bounds-force nodes bounds))
                (.stop))]
    (.tick sim force-iterations)
    (restore-order! nodes)))